/**
 * The notification bell in the app header: badge, popover, feed.
 *
 * Rendered conditionally by `AppLayout`, so when the flag is off this component's hooks
 * do not exist at all and nothing fetches. That is deliberate — `AppLayout` is always
 * mounted (docs/COMMON_BUGS.md on hooks auto-fetching in always-mounted components).
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import NotificationRow from "@/components/layout/NotificationRow";
import { HasDirectReports } from "@/lib/hooks/use-has-direct-reports";
import { useCurrentUser } from "@/lib/hooks/use-current-user";
import { useContactChangeActions } from "@/lib/hooks/use-contact-change-suggestion";
import type { FeedNotification } from "@/lib/notifications/types";
import { useNotificationFeed } from "@/lib/hooks/use-notification-feed";
import { badgeLabel } from "@/lib/notifications/badge";
import { useNotificationNavigate } from "@/lib/notifications/use-notification-navigate";
import type { NotificationTarget } from "@/lib/notifications/target";

interface Props {
  /**
   * Opens the compose dialog for a contact id. `AppLayout` owns that dialog, so the
   * one CTA this component cannot perform itself is handed back up.
   */
  onComposeEmail?: (contactId: string) => void;
}

export default function NotificationBell({ onComposeEmail }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  // Resolved job-change rows say "You…" or "<name>…" depending on who acted. Already loaded
  // app-wide by the header, so this reads the cache.
  const { currentUser } = useCurrentUser();
  // Decline straight from a job-change row. Mutations only — nothing is fetched until clicked.
  const { dismiss } = useContactChangeActions();
  /**
   * Rows declined (or found already resolved) in this session, patched locally. The backend
   * rewrites the same row in place, so the next feed poll agrees; this just stops the row
   * asking again until then.
   */
  const [patched, setPatched] = useState<Record<string, Record<string, string | number>>>({});
  const {
    notifications,
    unreadCount,
    newCount,
    loading,
    error,
    hasMore,
    loadMore,
    markRead,
    markAllRead,
    markSeen,
  } = useNotificationFeed({ enabled: true, open });

  /**
   * Opening the bell marks everything VIEWED, which clears the badge — and marks nothing read.
   *
   * Those are different questions. The badge asks "did anything arrive since I last looked";
   * read is per row and only the user clicking a row, or pressing Mark all read, answers it.
   * Marking read on open would turn the whole list grey the instant it appeared, so "what's
   * new" would become unanswerable — which is why the two are tracked separately
   * (`viewed_ts` / `read_ts`).
   *
   * Gated on newCount so merely reopening an already-viewed bell is not a mutation: this
   * component sits in an always-mounted subtree and gets opened idly.
   */
  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next && newCount > 0) {
        // Fire and forget: a failed mark must not stop the popover opening. The hook swallows
        // its own errors, and the next open retries.
        void markSeen();
      }
    },
    [newCount, markSeen],
  );

  const navigateToTarget = useNotificationNavigate({
    onComposeEmail,
    onNavigated: () => setOpen(false),
  });

  const handleActivate = (id: string) => (target: NotificationTarget) => {
    // Fire and forget: a failed mark-read must not block the navigation the user asked
    // for. The hook swallows its own errors.
    void markRead(id);
    navigateToTarget(target);
  };

  /** "Not now" on an open job-change question: personal, so it hides it for this user only. */
  const handleDecline = async (n: FeedNotification) => {
    if (n.target.kind !== "contactSuggestion") return;
    void markRead(n.id);
    try {
      const r = await dismiss(n.target.suggestionId);
      const s = r.suggestion;
      // A colleague may have resolved it a moment ago: show what they did, not a dismissal.
      const resolution = s && s.status === "accepted" && s.acceptMode
        ? { resolution: s.acceptMode, resolvedByUserId: s.actedByUserId ?? "", resolvedByName: s.actedByName ?? "" }
        : r.ok
          ? { resolution: "DISMISSED", resolvedByUserId: currentUser?.id ?? "" }
          : null;
      if (resolution) setPatched(prev => ({ ...prev, [n.id]: resolution }));
    } catch {
      // The row simply keeps asking; the review dialog offers the same choice.
    }
  };

  // Once the server's copy of a row carries its own resolution, it is the truth: drop the local
  // patch. Otherwise an Undo made in the review dialog would stay hidden behind it.
  useEffect(() => {
    const confirmed = notifications.filter(n => patched[n.id] && n.params.resolution);
    if (confirmed.length) {
      setPatched(prev => {
        const next = { ...prev };
        for (const n of confirmed) delete next[n.id];
        return next;
      });
    }
  }, [notifications, patched]);

  const rows = useMemo(
    () => notifications.map(n => (patched[n.id] ? { ...n, params: { ...n.params, ...patched[n.id] } } : n)),
    [notifications, patched],
  );

  const showEmptyState = !loading && !error && notifications.length === 0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t("common.notifications")}
          className="relative flex items-center justify-center h-8 w-8 rounded-md hover:bg-muted/50 transition text-muted-foreground hover:text-foreground"
        >
          <Bell className="h-4 w-4" />
          {newCount > 0 && (
            <span
              // newCount, not unreadCount: the badge counts what ARRIVED since the last
              // open, so it clears on open while rows stay unread. Server-authoritative
              // either way — the list is paginated, so counting loaded rows would
              // under-report past page one.
              aria-label={t("notifications.unread", { count: newCount })}
              className="absolute top-0.5 right-0.5 flex items-center justify-center h-4 min-w-4 px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold leading-none"
            >
              {badgeLabel(newCount)}
            </span>
          )}
        </button>
      </PopoverTrigger>

      {/* As tall as the window allows (Radix's available height, minus collisionPadding), not a
          fixed 400px: a fixed cap cut the list off a third of the way down a laptop screen. The
          header stays put and only the list scrolls. Same sizing as the demo (lovable) build. */}
      <PopoverContent
        side="bottom"
        align="end"
        collisionPadding={8}
        className="w-80 p-0 flex flex-col max-h-[var(--radix-popover-content-available-height)]"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <h3 className="text-sm font-semibold">{t("common.notifications")}</h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={() => void markAllRead()}
                className="text-xs text-accent hover:underline"
              >
                {t("common.markAllRead")}
              </button>
            )}
            <button
              type="button"
              aria-label={t("common.notificationSettings")}
              title={t("common.notificationSettings")}
              onClick={() => {
                setOpen(false);
                navigate("/settings?section=notifications");
              }}
              className="flex items-center justify-center h-6 w-6 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition"
            >
              <Settings className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto">
          {error && (
            <p className="px-4 py-6 text-center text-xs text-muted-foreground">
              {t("notifications.error")}
            </p>
          )}

          {showEmptyState && (
            // Synthetic, not a stored row. A real "welcome" notification would be
            // deleted by the 7-day purge, so it would vanish precisely for the quiet
            // users who still have nothing else to show — and it would have to decide
            // whether to count toward the badge. This does neither.
            <div className="px-4 py-8 text-center">
              <p className="text-sm font-medium">{t("notifications.empty.title")}</p>
              <p className="text-xs text-muted-foreground mt-1">{t("notifications.empty.body")}</p>
            </div>
          )}

          {/* One resolution for the whole list, and only while the popover is open.
              `useHasDirectReports` fetches the member list, this bell is always mounted in
              AppLayout, and calling it per row would be one request per notification — so
              the wrapper goes here, inside PopoverContent, which Radix unmounts on close. */}
          <HasDirectReports>
            {hasDirectReports =>
              rows.map(n => (
                <NotificationRow
                  key={n.id}
                  notification={n}
                  onActivate={handleActivate(n.id)}
                  hasDirectReports={hasDirectReports}
                  viewerUserId={currentUser?.id ?? null}
                  onDecline={() => void handleDecline(n)}
                />
              ))
            }
          </HasDirectReports>

          {hasMore && (
            <button
              type="button"
              onClick={() => void loadMore()}
              disabled={loading}
              className="w-full px-4 py-3 text-xs text-accent hover:underline disabled:opacity-50"
            >
              {t("notifications.loadMore")}
            </button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
